import { BaseHttpAdapter } from '~/services/adapters/base-http.adapter';
import {
    CreateReferralParams,
    FetchUserReferralsParams,
    GetReferralSuggestionsParams,
    ReferUserParams,
    Referral,
    SendReminderParams,
    TReferral
} from '~/components/Referral/components/ReferralsPage/types';
import {
    IReferralUserStatsResponse,
    TReferralSuggestion,
    IReferralUserBonusResponse
} from '~/types/server.types';
import { ReferralsAdapter } from '~/components/Referral/components/ReferralsPage/referralContext';

export class ReferralsHttpAdapter
    extends BaseHttpAdapter
    implements ReferralsAdapter
{
    async getUserReferralStats(
        userId: string
    ): Promise<IReferralUserStatsResponse> {
        return this.apiService.apiRequest({
            url: '/api/v2/referrals/user_referral_stats',
            params: { referrer_user_id: userId }
        });
    }

    async getUserReferralsBonus(): Promise<IReferralUserBonusResponse> {
        return this.apiService.apiRequest({
            url: '/api/v2/referrals/user_bonus_summary'
        });
    }

    async fetchUserReferrals({
        limit,
        skip,
        search = '',
        startDate,
        statuses,
        sortDirection
    }: FetchUserReferralsParams): Promise<TReferral[]> {
        const response = await this.apiService.apiRequest<TReferral[]>({
            method: 'get',
            url: '/api/v2/referrals/fetch_user_referrals',
            params: {
                skip,
                limit,
                search,
                start_date: startDate,
                statuses,
                sort_direction: sortDirection
            }
        });
        return response.map(referral => Referral.parse(referral));
    }

    async sendReminder({
        userId,
        referralId
    }: SendReminderParams): Promise<TReferral & Partial<TReferral>> {
        const response = await this.apiService.apiRequest<unknown>({
            method: 'put',
            url: '/api/v2/referrals/send_referral_reminder',
            params: { referral_id: referralId, user_id: userId }
        });
        return Referral.parse(response) as TReferral & Partial<TReferral>;
    }

    async referUser({
        userId,
        emails,
        message,
        subject
    }: ReferUserParams): Promise<TReferral[]> {
        return this.apiService.apiRequest<TReferral[]>({
            method: 'post',
            url: '/api/v2/referrals/refer_users_by_email',
            data: { emails, message, referrer_user_id: userId, subject }
        });
    }

    async createReferral({
        referredEmail,
        referrerEmail
    }: CreateReferralParams): Promise<TReferral> {
        const data = {
            referrer_email: referrerEmail,
            referred_email: referredEmail,
            source: 'suggestion',
            status: 'invited',
            send_email: true
        };
        return this.apiService.apiRequest<TReferral>({
            method: 'post',
            url: '/api/v2/referrals/create_referral',
            data
        });
    }

    async getReferralSuggestions({
        userId,
        limit,
        page
    }: GetReferralSuggestionsParams): Promise<TReferralSuggestion[]> {
        const params = {
            user_id: userId,
            limit,
            page
        };
        return this.apiService.apiRequest({
            method: 'get',
            url: '/api/v2/referrals/fetch_referral_suggestions',
            params
        });
    }

    async getAiReferralSuggestions({
        userId,
        limit,
        page
    }: GetReferralSuggestionsParams): Promise<TReferralSuggestion[]> {
        const params = {
            user_id: userId,
            limit,
            page
        };
        return this.apiService.apiRequest({
            method: 'get',
            url: '/api/v2/referrals/ai_suggestions',
            params
        });
    }

    dismissReferralSuggestion(email: string) {
        const params = {
            email
        };
        this.apiService.apiRequest({
            method: 'delete',
            url: '/api/v2/referrals/ai_suggestions',
            data: params
        });
    }

    async getCsvReferrals() {
        return this.apiService.apiRequest<{ url: string }>({
            method: 'get',
            url: '/api/v2/referrals/export_csv'
        });
    }
}
